import type { ResidentDetailTab } from '../types'
import type { ResidentDefaultFormValues } from '../types'

/** Tabs that share the resident/reimbursement form and can lose unsaved edits on leave. */
export const RESIDENT_TABS_WITH_UNSAVED_GUARD: readonly ResidentDetailTab[] = [
  'default',
  'calculation',
]

export const UNSAVED_RESIDENT_TAB_CHANGE_TITLE = 'Unsaved Changes'
export const UNSAVED_RESIDENT_TAB_CHANGE_MESSAGE =
  'You have unsaved changes in this tab. If you navigate to another tab without saving, your unsaved changes will be lost or reset.'

function hasDirtyFields(dirtyFields: unknown): boolean {
  if (!dirtyFields || typeof dirtyFields !== 'object') return false
  return Object.values(dirtyFields as Record<string, unknown>).some((value) => {
    if (value === true) return true
    if (Array.isArray(value)) return value.some(hasDirtyFields)
    return hasDirtyFields(value)
  })
}

const MONEY_FORM_FIELDS = new Set([
  'property_unused_security_deposit',
  'unused_security_deposit',
  'partial_payment_amount',
  'loss_adjustment',
])

const INTEGER_FORM_FIELDS = new Set([
  'first_month_not_paid_month',
  'first_month_not_paid_year',
  'lease_month',
])

function valueAtPath(source: unknown, path: string[]): unknown {
  return path.reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return undefined
    return (current as Record<string, unknown>)[key]
  }, source)
}

function normalizeMoneyValue(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const cleaned = raw.replace(/[^0-9.-]/g, '')
  if (!cleaned) return ''
  const num = Number(cleaned)
  return Number.isFinite(num) ? num.toFixed(2) : raw
}

function normalizeIntegerValue(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const num = Number(raw)
  return Number.isFinite(num) ? String(num) : raw
}

function normalizeDateValue(value: unknown): string {
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const ymd = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (ymd) {
    return `${ymd[1]}-${ymd[2].padStart(2, '0')}-${ymd[3].padStart(2, '0')}`
  }
  const mdy = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
  if (mdy) {
    return `${mdy[3]}-${mdy[1].padStart(2, '0')}-${mdy[2].padStart(2, '0')}`
  }
  return raw
}

function normalizeFormValue(fieldName: string, value: unknown): string {
  if (fieldName.toLowerCase().includes('date')) return normalizeDateValue(value)
  if (MONEY_FORM_FIELDS.has(fieldName)) return normalizeMoneyValue(value)
  if (INTEGER_FORM_FIELDS.has(fieldName)) return normalizeIntegerValue(value)
  return String(value ?? '').trim()
}

export function hasDirtyFieldValueChanges(args: {
  dirtyFields: unknown
  currentValues: Partial<ResidentDefaultFormValues>
  initialValues: Partial<ResidentDefaultFormValues>
  ignoredFields?: Iterable<string>
}): boolean {
  const ignoredFields = new Set(args.ignoredFields ?? [])
  const visit = (dirty: unknown, path: string[]): boolean => {
    if (!dirty || typeof dirty !== 'object') return false
    return Object.entries(dirty as Record<string, unknown>).some(([key, value]) => {
      const nextPath = [...path, key]
      if (value === true) {
        const fieldName = nextPath[nextPath.length - 1] ?? key
        if (ignoredFields.has(fieldName)) return false
        return (
          normalizeFormValue(fieldName, valueAtPath(args.currentValues, nextPath)) !==
          normalizeFormValue(fieldName, valueAtPath(args.initialValues, nextPath))
        )
      }
      if (Array.isArray(value)) return value.some((item, index) => visit(item, [...nextPath, String(index)]))
      return visit(value, nextPath)
    })
  }

  return visit(args.dirtyFields, [])
}

export function shouldConfirmUnsavedResidentTabChange(args: {
  currentTab: ResidentDetailTab
  nextTab: ResidentDetailTab
  isDirty: boolean
  dirtyFields?: unknown
}): boolean {
  if (args.nextTab === args.currentTab) return false
  if (!RESIDENT_TABS_WITH_UNSAVED_GUARD.includes(args.currentTab)) return false
  if (!args.isDirty) return false
  return args.dirtyFields === undefined ? true : hasDirtyFields(args.dirtyFields)
}
